import { Head, router } from "@inertiajs/react"
import { Plus } from "@untitledui/icons"

import { FilterChip, FilterRow } from "@/components/admin/filter-chip"
import { PageHeader } from "@/components/console/page-header"
import { Button } from "@/components/ui/button"
import { Table, TableCard } from "@/components/ui/table"
import { AdminLayout } from "@/layouts/admin-layout"
import {
  adminEnquiriesPath,
  adminEnquiryPath,
  adminSchoolPath,
  newAdminEnquiryPath,
} from "@/routes"

import {
  type EnquirySource,
  type EnquiryStatus,
  type EnquirySummary,
  STATUS_COLOR,
} from "./types"

interface Filters {
  status: string | null
  source: string | null
  sla: string | null
}

interface Props {
  enquiries: EnquirySummary[]
  filters: Filters
  available_filters: { statuses: EnquiryStatus[]; sources: EnquirySource[] }
  pagination: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
}

/** Who, school, source, status, arrived, SLA. */
const COLUMNS = 6

type GroupKey = "overdue" | "awaiting" | "answered"

type Item =
  | {
      kind: "group"
      id: `g-${GroupKey}`
      label: GroupKey
      count: number
      tier: "caution" | "quiet"
    }
  | { kind: "row"; id: number; group: GroupKey; enquiry: EnquirySummary }

const groupOf = (e: EnquirySummary): GroupKey =>
  e.first_response_at ? "answered" : e.sla_breached ? "overdue" : "awaiting"

/** Whole days since arrival, never less than one: the chip on an overdue row. */
const daysSince = (iso: string) =>
  Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000))

/** Grouped by state before coloured by state: overdue first, then awaiting, then answered. */
const groupItems = (enquiries: EnquirySummary[]): Item[] => {
  const order: { key: GroupKey; tier: "caution" | "quiet" }[] = [
    { key: "overdue", tier: "caution" },
    { key: "awaiting", tier: "quiet" },
    { key: "answered", tier: "quiet" },
  ]
  return order.flatMap(({ key, tier }) => {
    const rows = enquiries.filter((e) => groupOf(e) === key)
    if (rows.length === 0) return []
    return [
      { kind: "group", id: `g-${key}`, label: key, count: rows.length, tier },
      ...rows.map((e) => ({
        kind: "row" as const,
        id: e.id,
        group: key,
        enquiry: e,
      })),
    ]
  })
}

export default function AdminEnquiriesIndex({
  enquiries,
  filters,
  available_filters,
  pagination,
}: Props) {
  const visitWith = (next: Partial<Filters & { page: number }>) => {
    const merged = { ...filters, ...next }
    const data: Record<string, string | number> = {}
    if (merged.status) data.status = merged.status
    if (merged.source) data.source = merged.source
    if (merged.sla) data.sla = merged.sla
    if (next.page && next.page > 1) data.page = next.page
    router.visit(adminEnquiriesPath(), {
      data,
      preserveState: true,
      preserveScroll: true,
      only: ["enquiries", "filters", "pagination"],
    })
  }

  const items = groupItems(enquiries)
  const overdue = enquiries.filter((e) => groupOf(e) === "overdue").length

  const tokens = [
    filters.status && {
      label: `status: ${filters.status.toLowerCase()}`,
      onClear: () => visitWith({ status: null, page: 1 }),
    },
    filters.source && {
      label: `source: ${filters.source.replace(/_/g, " ").toLowerCase()}`,
      onClear: () => visitWith({ source: null, page: 1 }),
    },
    filters.sla && {
      label: `sla: ${filters.sla.toLowerCase()}`,
      onClear: () => visitWith({ sla: null, page: 1 }),
    },
  ].filter((token): token is { label: string; onClear: () => void } =>
    Boolean(token),
  )

  return (
    <AdminLayout>
      <Head title="Enquiries" />
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          crumbs={[{ label: "call list" }, { label: "enquiries" }]}
          title="Enquiries"
          description="Answer inside a business day."
          actions={
            <Button
              size="md"
              color="primary"
              iconLeading={Plus}
              href={newAdminEnquiryPath()}
            >
              File an enquiry
            </Button>
          }
        />

        <div className="flex flex-col gap-3">
          <FilterRow label="Status">
            <FilterChip
              active={!filters.status}
              onClick={() => visitWith({ status: null, page: 1 })}
            >
              All
            </FilterChip>
            {available_filters.statuses.map((s) => (
              <FilterChip
                key={s}
                active={filters.status === s}
                onClick={() => visitWith({ status: s, page: 1 })}
              >
                {s}
              </FilterChip>
            ))}
          </FilterRow>
          <FilterRow label="Source">
            <FilterChip
              active={!filters.source}
              onClick={() => visitWith({ source: null, page: 1 })}
            >
              All
            </FilterChip>
            {available_filters.sources.map((s) => (
              <FilterChip
                key={s}
                active={filters.source === s}
                onClick={() => visitWith({ source: s, page: 1 })}
              >
                {s.replace(/_/g, " ")}
              </FilterChip>
            ))}
          </FilterRow>
          <FilterRow label="SLA">
            <FilterChip
              active={!filters.sla}
              onClick={() => visitWith({ sla: null, page: 1 })}
            >
              All
            </FilterChip>
            <FilterChip
              active={filters.sla === "awaiting"}
              onClick={() => visitWith({ sla: "awaiting", page: 1 })}
            >
              Awaiting a response
            </FilterChip>
          </FilterRow>
        </div>

        <TableCard.Root bleed>
          <TableCard.Caption
            title="Enquiries"
            count={`${pagination.total} ${pagination.total === 1 ? "enquiry" : "enquiries"}`}
            alert={overdue > 0 ? `${overdue} overdue` : undefined}
            tokens={tokens}
            note="arrived ↓"
          />
          {enquiries.length === 0 ? (
            <TableCard.Empty>
              No enquiries here. Clear a filter, or file one from a call or an
              email.
            </TableCard.Empty>
          ) : (
            <>
              <Table aria-label="Enquiries">
                <Table.Header>
                  <Table.Head
                    id="who"
                    isRowHeader
                    label="Who"
                    className="w-full"
                  />
                  <Table.Head id="school" label="School" />
                  <Table.Head id="source" label="Source" />
                  <Table.Head id="status" label="Status" />
                  <Table.Head
                    id="age"
                    label="Arrived"
                    align="right"
                    sorted="desc"
                  />
                  <Table.Head id="sla" label="SLA" />
                </Table.Header>
                <Table.Body items={items}>
                  {(item) =>
                    item.kind === "group" ? (
                      <Table.Group
                        id={item.id}
                        label={item.label}
                        count={item.count}
                        tier={item.tier}
                        span={COLUMNS}
                      />
                    ) : (
                      <Table.Row
                        id={item.id}
                        href={adminEnquiryPath(item.enquiry.id)}
                        tone={item.group === "overdue" ? "caution" : undefined}
                      >
                        <Table.Lead>
                          {item.enquiry.name ??
                            item.enquiry.email ??
                            item.enquiry.phone ??
                            "—"}
                        </Table.Lead>
                        <Table.Cell>
                          {item.enquiry.matched_school ? (
                            <Table.Link
                              href={adminSchoolPath(
                                item.enquiry.matched_school.id,
                              )}
                            >
                              {item.enquiry.matched_school.name}
                            </Table.Link>
                          ) : (
                            <>
                              {item.enquiry.school_name_as_typed ?? "—"}{" "}
                              (unmatched)
                            </>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {item.enquiry.source.replace(/_/g, " ")}
                        </Table.Cell>
                        <Table.Status tier={STATUS_COLOR[item.enquiry.status]}>
                          {item.enquiry.status}
                        </Table.Status>
                        <Table.When
                          at={item.enquiry.received_at}
                          relative
                          chip={
                            item.group === "overdue" ? (
                              <Table.Chip tier="caution">
                                +{daysSince(item.enquiry.received_at)}d
                              </Table.Chip>
                            ) : undefined
                          }
                        />
                        {item.group === "answered" ? (
                          <Table.Status tier="confirmed">answered</Table.Status>
                        ) : item.group === "overdue" ? (
                          <Table.Status tier="caution">past due</Table.Status>
                        ) : (
                          <Table.Status tier="caution">due</Table.Status>
                        )}
                      </Table.Row>
                    )
                  }
                </Table.Body>
              </Table>
              <TableCard.Footer
                shown={enquiries.length}
                total={pagination.total}
                trailing={
                  pagination.total_pages > 1 && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        color="secondary"
                        isDisabled={pagination.page <= 1}
                        onPress={() => visitWith({ page: pagination.page - 1 })}
                      >
                        Previous
                      </Button>
                      <Button
                        size="sm"
                        color="secondary"
                        isDisabled={pagination.page >= pagination.total_pages}
                        onPress={() => visitWith({ page: pagination.page + 1 })}
                      >
                        Next
                      </Button>
                    </div>
                  )
                }
              />
            </>
          )}
        </TableCard.Root>
      </div>
    </AdminLayout>
  )
}
